import RadioGroup from '@mui/material/RadioGroup';
import * as _ from 'lodash';
import moment from 'moment';
import { Component } from 'react';
import {
  ButtonGroup,
  Checkbox,
  ControlLabel,
  DropdownButton,
  FormGroup,
  ListGroup,
  ListGroupItem,
  MenuItem,
} from 'react-bootstrap';
import { connect } from 'react-redux';

import { REQUIRE_TEAM_ON_UPLOAD } from '../../../shared/featureFlags/flagNames';
import { window } from '../../globals';
import { isFeatureEnabled } from '../../helpers/features';
import { userHasPermissionToViewAnyProject } from '../../helpers/permissions';
import { get } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import { LabeledRadio } from '../generic/Forms';
import Icon, { icons, IconText } from '../generic/Icon';
import Link from '../generic/Link';
import Loader from '../generic/Loader';
import Text from '../generic/Text';
import SelectTeam, { teamsUserCanAddProject } from '../projects/SelectTeam';
import UpdateHookConfig from '../projects/UpdateHookConfig';
import type { Repository, Team, UpdateHook } from '../../types';
import type { ProjectImportOptions } from '../projects/import/types';

import ModalWizard from './ModalWizard';

const PAGE_SIZE = 100;

type IModalBulkImportState = {
  repos: Repository[];
  loading?: boolean;
  error?: Error;
  filter_access?: string;
  filter_updated?: number;
  filter_forked?: boolean;
  policy_notifications?: string;
  policy_update?: string;
  policy_access?: string;
  send_badge_pr?: boolean;
  update_hook?: UpdateHook;
  selectedTeams: Team[];
};

type IModalBulkImportProps = {
  allTeams: Team[];
  fetchAllTeams: (filters: object) => void;

  service?: string;
  filterName?: string;
  filterType?: string;
  filterValue?: string;
  instanceName?: string;
  params?: any;
  show?: boolean;
  onHide: Function;
  onFinish?: (repos: any[], options: ProjectImportOptions) => void;
};

// These are here for if they're ever needed,
// but for now they aren't, so keeping them commented.
// const IMPORT_STEP_REVIEW_PROJECTS = 1;
// const IMPORT_STEP_CONFIGURE_IMPORT = 2;
const IMPORT_STEP_SELECT_TEAMS = 3;

class ModalBulkImport extends Component<IModalBulkImportProps, IModalBulkImportState> {
  constructor(props) {
    super(props);
    this.state = {
      repos: [],
      loading: true,
      error: null,

      // filters
      filter_access: 'all',
      filter_updated: null,
      filter_forked: false,

      policy_notifications: 'true',
      policy_update: 'always',
      policy_access: 'default',

      send_badge_pr: false,

      update_hook: null, // submit if policy_update : true

      selectedTeams: [],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.show && !this.props.show) {
      this.loadAllRepos(nextProps);
    }
  }

  componentDidMount() {
    this.props.fetchAllTeams({ organizationId: window.user.organizationId });
  }

  loadRepoPage = (props: IModalBulkImportProps, page?: number, repos?: Repository[]): Promise<Repository[]> => {
    const defaultedPage = page ?? 1;
    const defaultedRepos = repos ?? [];
    const defaultedProps = props ?? this.props;

    return get<Repository[]>(
      `/api/services/${defaultedProps.service}/repositories`,
      false,
      {
        page: defaultedPage,
        pageSize: PAGE_SIZE,
        q: null,
        filterType: defaultedProps.filterType,
        filterValue: defaultedProps.filterValue,
        instanceName: defaultedProps.instanceName,
      },
    ).then((results: { data?: Repository[] }) => {
      const allRepos = defaultedRepos.concat(results.data || []);

      // If we fetched a full page, fetch the next page.
      if (results.data?.length === PAGE_SIZE && defaultedPage < 10) {
        return this.loadRepoPage(defaultedProps, defaultedPage + 1, allRepos);
      }

      return allRepos;
    });
  };

  loadAllRepos = props => {
    this.setState({ error: null, loading: true });
    this.loadRepoPage(props || this.props).then(
      repos => {
        this.setState({ repos, loading: false });
      },
      error => {
        this.setState({ error, loading: false });
      },
    );
  };

  renderImport = (repos: Repository[]) => {
    const REPO_LIST_LENGTH = 7;
    return (
      <div>
        <div className="solution-banner">
          <h4 style={{ marginBottom: '5px' }}>
            <IconText
              icon={icons.warning}
              text={`FOSSA will import ${repos.length} repositories`}
            />
          </h4>
          {repos.length === 1000 && (
            <Text.Micro>
              Note: Due to GitHub API limits, FOSSA will only import the first 1000 repositories.
            </Text.Micro>
          )}
          <b className="inline-header">Filter:</b>
          <br />
          <ButtonGroup>
            <DropdownButton
              id="import-repository-privacy-filter"
              title={
                (
                  <span>
                    {this.state.filter_access === 'all' ? 'Public & private' : 'Only ' + this.state.filter_access}
                  </span>
                ) as any
              }
              onSelect={((filter_access) => this.setState({ filter_access })) as any}>
              <MenuItem eventKey="all" active={this.state.filter_access === 'all'}>
                All
              </MenuItem>
              <MenuItem eventKey="public" active={this.state.filter_access === 'public'}>
                Public
              </MenuItem>
              <MenuItem eventKey="private" active={this.state.filter_access === 'private'}>
                Private
              </MenuItem>
            </DropdownButton>
            <DropdownButton
              id="import-repository-last-updated-filter"
              title={
                (
                  <span>
                    Updated{' '}
                    {this.state.filter_updated
                      ? moment()
                        .subtract(this.state.filter_updated, 'days')
                        .fromNow()
                      : 'anytime'}
                  </span>
                ) as any
              }
              onSelect={((filter_updated) => this.setState({ filter_updated })) as any}>
              <MenuItem eventKey={null} active={this.state.filter_updated === null}>
                Anytime
              </MenuItem>
              <MenuItem eventKey={30} active={this.state.filter_updated === 30}>
                Last 30 days
              </MenuItem>
              <MenuItem eventKey={90} active={this.state.filter_updated === 90}>
                Last 90 days
              </MenuItem>
              <MenuItem eventKey={365} active={this.state.filter_updated === 365}>
                Last year
              </MenuItem>
            </DropdownButton>
            <DropdownButton
              id="import-repository-include-forks-filter"
              title={<span>Forks {!this.state.filter_forked ? 'included' : 'excluded'}</span> as any}
              onSelect={((filter_forked) => this.setState({ filter_forked })) as any}>
              <MenuItem eventKey={false} active={!this.state.filter_forked}>
                Included
              </MenuItem>
              <MenuItem eventKey active={this.state.filter_forked}>
                Excluded
              </MenuItem>
            </DropdownButton>
          </ButtonGroup>
        </div>
        <br />
        {repos.length > 0 ? (
          <ListGroup>
            {repos.slice(0, REPO_LIST_LENGTH).map((repo, index) => (
              <ListGroupItem key={repo.id || index}>
                <Link
                  to={repo.url}
                  openInNewTab
                >
                  {repo.title}{' '} <Icon icon={icons.externalLink} />
                </Link>
              </ListGroupItem>
            ))}
            {repos.length > REPO_LIST_LENGTH ? (
              <ListGroupItem className="text-center">
                <i>and {repos.length - REPO_LIST_LENGTH} more...</i>
              </ListGroupItem>
            ) : null}
          </ListGroup>
        ) : (
          <div className="alert">No repos to preview</div>
        )}
      </div>
    );
  };

  onTeamsSelected = (teams: Team[]) => {
    this.setState({ selectedTeams: teams });
  };

  canMoveToNextWizardStep = (currentStep: number) => {
    if (this.state.loading || this.state.error) {
      return false;
    }

    return currentStep !== IMPORT_STEP_SELECT_TEAMS ||
      userHasPermissionToViewAnyProject() ||
      this.state.selectedTeams.length > 0;
  };

  showWizardStep = (step: number) => {
    if (step !== IMPORT_STEP_SELECT_TEAMS) {
      return true;
    }

    return teamsUserCanAddProject(this.props.allTeams).length > 0;
  };

  toggleSendBadgePR = () => this.setState(state => ({
    send_badge_pr: !state.send_badge_pr,
  }));

  render() {
    const repos = this.state.repos.filter(repo => {
      if (this.state.filter_forked && repo.isFork) {
        return false;
      }

      if (this.state.filter_access === 'public' && repo.isPrivate) {
        return false;
      }

      if (this.state.filter_access === 'private' && !repo.isPrivate) {
        return false;
      }

      if (
        this.state.filter_updated &&
        moment()
          .subtract(this.state.filter_updated, 'days')
          .isAfter(repo.updated_at)
      ) {
        return false;
      }

      if (repo.project) {
        return false;
      }

      // Do not allow users to import their empty repositories.
      const branches = repo.branches || [];
      if (!repo.branch || branches.length === 0) {
        return false;
      }

      return true;
    });

    // Fallback to using the active filter's value if it doesn't have a name.
    const filterName = this.props.filterName ?? this.props.filterValue;

    return (
      <ModalWizard
        showStep={this.showWizardStep}
        canNextStep={this.canMoveToNextWizardStep}
        {...this.props}
        title={`Import all repositories from "${filterName}"`}
        stepNames={['Review Projects', 'Configure Import', 'Select Teams']}
        finishText={
          <span>
            Confirm Import <b>{repos.length}</b> Repos
          </span>
        }
        onFinish={() =>
          this.props.onFinish(
            repos,
            _.pick(
              this.state,
              'policy_notifications',
              'policy_update',
              'policy_access',
              'update_hook',
              'send_badge_pr',
              'selectedTeams',
            ),
          )}>
        <div>
          {!this.state.loading ? (
            this.state.error ? (
              <div className="card card-error">{this.state.error.message}</div>
            ) : (
              this.renderImport(repos)
            )
          ) : (
            <div className="card card-primary text-center">
              <Loader color="#d2b690" />
              <h4>
                Gathering all repositories in <b>{filterName}</b> for review...
              </h4>
              <p>This could take a couple minutes.</p>
            </div>
          )}
        </div>
        <div>
          <FormGroup className="card">
            <h4>Notifications</h4>
            <RadioGroup
              name="policy_notifications"
              defaultValue={this.state.policy_notifications}
              onChange={event => this.setState({
                policy_notifications: event.target.value,
              })}
            >
              <LabeledRadio value="true" label="Enable notifications (recommended for &lt;20 repos)" />
              <LabeledRadio value="false" label="Configure notifications later" />
            </RadioGroup>
          </FormGroup>
          <FormGroup className="card">
            <h4>Updates</h4>
            <RadioGroup
              name="policy_update"
              defaultValue={this.state.policy_update}
              onChange={event => {
                const policy_update = event.target.value;
                const update_hook = policy_update === 'scheduled'
                  ? {
                      type: 'scheduled',
                      scheduled_interval: null,
                      scheduled_interval_length: null,
                      scheduled_interval_time: null,
                    }
                  : null;
                this.setState({ policy_update, update_hook });
              }}>
              <LabeledRadio value="always" label="Per-commit" />
              <LabeledRadio value="organization" label="Use organization defaults" />
              <LabeledRadio value="scheduled" label="Scheduled update (recommended for &gt;20 repos)" />
            </RadioGroup>
            {this.state.policy_update === 'scheduled' ? (
              <UpdateHookConfig
                updateHook={this.state.update_hook}
                project={{}}
                project_id="shim_value"
                onUpdateHookChange={update_hook => this.setState({ update_hook })}
              />
            ) : null}
          </FormGroup>
          <FormGroup className="card">
            <h4>Access</h4>
            <RadioGroup
              name="policy_access"
              defaultValue={this.state.policy_access}
              onChange={event => this.setState({
                policy_access: event.target.value,
              })}
            >
              <LabeledRadio value="default" label="Default (as-is based off repo permissions)" />
              <LabeledRadio value="public" label="Set all public" />
              <LabeledRadio value="private" label="Set all private" />
            </RadioGroup>
          </FormGroup>
          <FormGroup className="card">
            <ControlLabel>Misc</ControlLabel>
            <Checkbox
              checked={this.state.send_badge_pr}
              onChange={this.toggleSendBadgePR}
            >
              Submit badge PRs after import (public GitHub READMEs only)
            </Checkbox>
          </FormGroup>
        </div>
        <div>
          <h4>Assign Project to Teams</h4>
          <SelectTeam
            allTeams={this.props.allTeams}
            selectedTeams={this.state.selectedTeams}
            onTeamsSelected={this.onTeamsSelected}
            permissionKind="project"
            isRequired={isFeatureEnabled(REQUIRE_TEAM_ON_UPLOAD)}
          />
        </div>
      </ModalWizard>
    );
  }
}

const mapStateToProps = state => ({
  allTeams: TeamsModule.selectors.getTeamList(state),
});

const mapDispatchToProps = {
  fetchAllTeams: TeamsModule.actions.fetchAll,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalBulkImport);
